import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { apiClient } from "../../services/api/apiService";
import "../../styles/features/auth.css";

function VerifyEmail() {
  const [status, setStatus] = useState("verifying"); // verifying | success | already | error
  const [message, setMessage] = useState("");
  const location = useLocation();

  useEffect(() => {
    const token = new URLSearchParams(location.search).get("token");
    if (!token) {
      setStatus("error");
      setMessage("No verification token found in the link.");
      return;
    }

    apiClient
      .get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then((res) => {
        if (res.data?.message?.includes("already")) {
          setStatus("already");
        } else {
          setStatus("success");
        }
        setMessage(res.data?.message || "Email verified.");
      })
      .catch((err) => {
        setStatus("error");
        setMessage(
          err.response?.data?.message ||
            "Invalid or expired verification link. Please request a new one."
        );
      });
  }, [location.search]);

  const icon = {
    verifying: "⏳",
    success: "✅",
    already: "✅",
    error: "❌",
  }[status];

  return (
    <div className="auth-container">
      <div className="auth-form" style={{ textAlign: "center" }}>
        <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>{icon}</div>
        <h2>
          {status === "verifying" && "Verifying your email…"}
          {status === "success" && "Account Activated!"}
          {status === "already" && "Already Verified"}
          {status === "error" && "Verification Failed"}
        </h2>
        <p style={{ color: "#555", marginBottom: "1.5rem" }}>{message}</p>

        {(status === "success" || status === "already") && (
          <Link
            to="/login"
            className="auth-button"
            style={{ display: "inline-block", textDecoration: "none" }}
          >
            Go to Login
          </Link>
        )}

        {status === "error" && (
          <>
            <ResendVerification />
            <div style={{ marginTop: "1rem" }}>
              <Link to="/register" style={{ color: "#007bff" }}>
                Back to Register
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ResendVerification() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => {
      setCooldown((c) => { if (c <= 1) { clearInterval(timerRef.current); return 0; } return c - 1; });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [cooldown]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!email || cooldown > 0) return;
    setSending(true);
    setMsg("");
    try {
      await apiClient.post("/auth/resend-verification", { email });
      setMsg("✅ Verification email sent! Check your inbox and spam folder.");
      setCooldown(30);
    } catch (err) {
      setMsg(err.response?.data?.message || "Failed to send email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleResend} style={{ marginTop: "1.5rem" }}>
      <p style={{ color: "#555", marginBottom: "0.5rem" }}>
        Request a new activation link:
      </p>
      <div className="form-group">
        <label htmlFor="resend-email">Your Email</label>
        <input
          id="resend-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <button
        type="submit"
        className="auth-button"
        disabled={sending || cooldown > 0}
        style={{ background: cooldown > 0 ? "#ccc" : undefined, cursor: cooldown > 0 ? "not-allowed" : "pointer" }}
      >
        {sending ? "Sending…" : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend Activation Email"}
      </button>
      {msg && (
        <p style={{ color: msg.includes("sent") ? "#28a745" : "#dc3545", marginTop: "0.5rem" }}>
          {msg}
        </p>
      )}
    </form>
  );
}

export default VerifyEmail;
